import { supabase } from "@/shared/lib/supabaseClient";
import type { Database } from "@/shared/types/database.types";

export type Categoria = Database["public"]["Tables"]["categorias"]["Row"];

export const categoriasService = {
  async listarTodas(): Promise<Categoria[]> {
    const { data, error } = await supabase
      .from("categorias")
      .select("*")
      .order("nome", { ascending: true });

    if (error) throw new Error("Não foi possível carregar as categorias.");
    return data || [];
  },

  async criar(nome: string): Promise<Categoria> {
    const nomeNormalizado = nome.trim();

    const { data, error } = await supabase
      .from("categorias")
      .insert({ nome: nomeNormalizado })
      .select()
      .single();

    if (error) {
      if (error.code === "23505") {
        throw new Error(`Já existe uma categoria com o nome "${nomeNormalizado}".`);
      }
      throw new Error("Não foi possível criar a categoria.");
    }

    if (!data) throw new Error("Algo deu errado ao carregar a categoria. Tente novamente.");
    return data;
  },

  /**
   * Versão usada no FormularioLivro (inline): retorna a categoria existente
   * em vez de lançar erro, para não interromper o fluxo de cadastro do livro.
   */
  async criarOuBuscarExistente(nome: string): Promise<Categoria> {
    const nomeNormalizado = nome.trim();

    const { data, error } = await supabase
      .from("categorias")
      .insert({ nome: nomeNormalizado })
      .select()
      .single();

    if (error) {
      if (error.code === "23505") {
        const existente = await supabase
          .from("categorias")
          .select("*")
          .eq("nome", nomeNormalizado)
          .single();
        if (existente.data) return existente.data;
      }
      throw new Error("Não foi possível criar a categoria.");
    }

    if (!data) throw new Error("Algo deu errado ao carregar a categoria. Tente novamente.");
    return data;
  },

  async atualizar(id: string, nome: string): Promise<void> {
    const { error } = await supabase.from("categorias").update({ nome: nome.trim() }).eq("id", id);

    if (error) {
      if (error.code === "23505") {
        throw new Error("Já existe outra categoria com esse nome.");
      }
      throw new Error("Não foi possível atualizar a categoria.");
    }
  },

  /**
   * Exclui uma categoria, bloqueado se houver qualquer livro (não excluído)
   * vinculado a ela — mesma regra de proteção usada em livrosService.excluir
   * para empréstimos em curso. UX-06: agora checa o vínculo N:N
   * (livros_categorias) em vez da antiga coluna livros.categoria_id.
   */
  async excluir(id: string): Promise<void> {
    const { count, error: errorLivros } = await supabase
      .from("livros_categorias")
      .select("livro_id, livros!inner(deleted_at)", { count: "exact", head: true })
      .eq("categoria_id", id)
      .is("livros.deleted_at", null);

    if (errorLivros) {
      throw new Error("Não foi possível verificar livros associados a esta categoria.");
    }

    if (count && count > 0) {
      throw new Error(
        "Esta categoria não pode ser excluída: há livros cadastrados nela. Mova ou remova esses livros primeiro.",
      );
    }

    const { error } = await supabase.from("categorias").delete().eq("id", id);

    if (error) throw new Error("Não foi possível excluir a categoria.");
  },
};
